<template>
  <v-container
    id="handleDropUpload"
  >
    <v-row
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          {{ uploadActive ? 'Upload Songs' : 'Browse Music Library' }}
        </div>
      </v-col>
      <v-spacer />
      <v-col
        v-if="!uploadActive"
        cols="auto"
      >
        <v-text-field
          v-model="filters.search"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="!uploadActive"
          dark
          color="primary"
          class="ma-0"
          @click="uploadActive = true"
        >
          <v-icon
            left
            small
          >
            fas fa-upload
          </v-icon>
          Upload Music
        </v-btn>
        <v-btn
          v-else
          text
          color="primary"
          class="ma-0"
          @click="uploadActive = false"
        >
          <v-icon
            left
            small
          >
            fas fa-arrow-left
          </v-icon>
          Return to library
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!--<editor-fold desc="Filters">-->
      <filters-column
        :filters-active="filtersActive"
        :title="uploadActive ? 'Attributes For Uploaded Songs' : 'Apply Filters'"
        :emphasized="uploadActive"
        @filters:clear-all="clearFilters()"
      >
        <div
          v-for="attribute in attributes"
          :key="attribute.id"
        >
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="font-weight-medium body-2 grey--text text--darken-1 px-4 my-2">
                {{ attribute.title }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pr-4 pl-2"
            >
              <v-btn
                outlined
                x-small
                :to="{ name: 'attributes' }"
                target="_blank"
              >
                Edit
              </v-btn>
            </v-col>
          </v-row>

          <v-list
            rounded
            color="transparent"
            class="pa-0 mb-4"
            dense
          >
            <v-list-item-group
              v-model="filters.attributes[attribute.id]"
              class="ma-0"
              multiple
            >
              <v-list-item
                v-for="value in attribute.values"
                :key="value.id"
                class="mb-0"
                active-class="primary--text"
                :value="value.id"
              >
                <template v-slot:default="{ active, toggle }">
                  <v-list-item-action>
                    <v-simple-checkbox
                      color="primary"
                      :value="active"
                      :ripple="false"
                      @click="toggle"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ value.value }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col v-show="!uploadActive">
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
          :show-counts="!filtersActive && selected.length === 0"
          :has-actions="filtersActive || selected.length > 0"
          :total-visible="8"
        >
          <template
            v-if="filtersActive || selected.length > 0"
            v-slot:action
          >
            <v-row
              dense
              class="caption"
              align="center"
            >
              <v-col
                cols="auto"
                style="min-width: 130px;"
              >
                <span v-if="selected.length > 0">With <strong>{{ selected.length }} selected</strong></span>
                <span v-else>With <strong>{{ items.meta.total }} filtered</strong></span>
              </v-col>
              <v-col
                v-if="selected.length > 0"
                cols="auto"
              >
                <v-btn
                  outlined
                  class="text-none"
                  small
                  color="secondary"
                  @click="editFilesAttributesDialog = true"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-pen
                  </v-icon>
                  Change Attributes
                </v-btn>
              </v-col>
              <v-col
                v-if="Object.keys(filters.attributes).length > 0"
                cols="auto"
              >
                <v-btn
                  outlined
                  class="text-none"
                  small
                  color="secondary"
                  @click="editPlaylistDialog = true"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-plus
                  </v-icon>
                  Create a Playlist
                </v-btn>
              </v-col>
              <v-col
                v-if="selected.length > 0"
                cols="auto"
              >
                <v-btn
                  outlined
                  class="text-none"
                  small
                  color="secondary"
                  @click="addToPlaylistDialog.visible = true"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-link
                  </v-icon>
                  Add to Playlist
                </v-btn>
              </v-col>
              <v-col
                v-if="items.meta.total > 0 || selected.length > 0"
                cols="auto"
              >
                <v-btn
                  icon
                  color="grey"
                  class="ma-0"
                  small
                  @click="toggleConfirmationDialog(true)"
                >
                  <v-icon x-small>
                    fa fa-trash
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </pagination>
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          show-select
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: file, isSelected, select }">
            <tr>
              <td>
                <v-simple-checkbox
                  :value="isSelected"
                  class="show-on-hover"
                  :class="{ 'visible': isSelected }"
                  color="primary"
                  :ripple="false"
                  @input="select"
                />
              </td>
              <td @click="select">
                {{ file.artist }}
              </td>
              <td @click="select">
                {{ file.title }}
              </td>
              <td @click="select">
                <div v-if="file.attribute_values.length > 0">
                  <v-chip
                    v-for="value in file.attribute_values"
                    :key="value.id"
                    x-small
                    outlined
                    class="mr-1"
                  >
                    {{ value.value }}
                  </v-chip>
                </div>
                <div
                  v-else
                  class="text--disabled caption"
                >
                  None
                </div>
              </td>
              <td style="width: 1px">
                <audio
                  :key="'audio-player-' + file.id"
                  style="height: 26px"
                  controls=""
                  preload="none"
                >
                  <source
                    :src="file.url"
                    type="audio/mpeg"
                  >
                </audio>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item @click="editLibraryFileDialog.file = file; editLibraryFileDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Song</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="selected = [ file ]; deleteConfirmationDialogVisible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Delete</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
          />
        </div>
      </v-col>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Upload">-->
      <v-col v-if="uploadActive">
        <file-uploader
          ref="fileUploader"
          v-model="filesDirty"
          custom-drag-handler="#handleDropUpload"
          multiple
          accept="audio/*"
          :upload-url="uploadUrl"
          :additional-form-data="{ type: TYPE_MUSIC, attribute_values: uploadAttributeValues }"
          library-uploader
          @drag:show="dropFilesOverlay = true"
          @drag:hide="dropFilesOverlay = false"
          @progress="totalUploadProgress = $event"
        />
        <!--        <div-->
        <!--          v-if="errors.length > 0"-->
        <!--          class="caption error&#45;&#45;text"-->
        <!--        >-->
        <!--          {{ errors[0] }}-->
        <!--        </div>-->
      </v-col>
      <!--</editor-fold>-->
    </v-row>

    <v-overlay
      :value="dropFilesOverlay"
      absolute
      color="white"
      light
      opacity="0.7"
    >
      <div class="text-center grey--text text--darken-2 mb-4">
        <v-icon
          x-large
          color="grey darken-2"
        >
          fa fa-clipboard-list
        </v-icon>
      </div>
      <div class="title grey--text text--darken-2">
        Drop files here
      </div>
    </v-overlay>

    <v-footer
      v-if="uploadActive && filesDirty.length > 0"
      fixed
      color="primary"
      padless
      elevation="10"
      class="no-gutters"
    >
      <v-col>
        <v-progress-linear
          color="white"
          background-opacity="0.7"
          height="5px"
        />
        <div class="white--text pa-3 body-2">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              Apply attributes on the left and start upload. You will add <strong>{{ filesDirty.length }} new songs</strong>.
            </v-col>
            <v-col
              cols="auto"
              class="pr-2"
            >
              <v-progress-circular
                color="white"
                :size="30"
                :width="1"
                :value="totalUploadProgress"
              />
            </v-col>
            <v-col
              v-if="uploading"
              cols="auto"
              class="pr-2"
            >
              Uploading &middot; {{ Math.round(totalUploadProgress) }}%
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="white"
                text
                class="my-0 ml-0 mr-1"
                :disabled="uploading"
                @click="filesDirty = []"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-times
                </v-icon>
                Remove all files
              </v-btn>
              <v-btn
                color="secondary"
                class="ma-0"
                :disabled="uploading"
                @click="startUpload()"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-play
                </v-icon>
                Start upload
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-footer>

    <edit-playlist-dialog
      :visible.sync="editPlaylistDialog"
      :selected-library-files="selected"
      :selected-attributes="filters.attributes"
    />

    <edit-files-attributes-dialog
      v-if="selected.length > 0"
      :visible.sync="editFilesAttributesDialog"
      :selected-library-files="selected"
      @updated="fetchItems()"
    />

    <edit-library-file-dialog
      v-if="editLibraryFileDialog.file"
      :file="editLibraryFileDialog.file"
      :visible.sync="editLibraryFileDialog.visible"
      @file:updated="fetchItems()"
    />

    <add-to-playlist-dialog
      :preselected-playlist-id="addToPlaylistDialog.playlistId"
      :visible.sync="addToPlaylistDialog.visible"
      :selected-library-files="selected"
    />

    <confirm-delete-dialog
      text="Are you sure you want to delete the selected files?"
      :submitting="deleting"
      :visible.sync="deleteConfirmationDialogVisible"
      @confirm="deleteFiles(selected.map(f => f.id))"
    />
  </v-container>
</template>

<script>
// @ is an alias to /src

import FiltersColumn from '@/components/helpers/FiltersColumn'
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import { cloneDeep } from 'lodash'
import LibraryFileModel from '@/models/LibraryFileModel'
import LibraryFiles from '@/services/LibraryFiles'
import FileUploader from '@/components/helpers/FileUploader'
import EditPlaylistDialog from '@/components/playlists/EditPlaylistDialog'
import AddToPlaylistDialog from '@/components/playlists/AddToPlaylistDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import EditFilesAttributesDialog from '@/components/library/EditFilesAttributesDialog'
import EditLibraryFileDialog from '@/components/library/EditLibraryFileDialog'

export default {
  name: 'MusicLibrary',
  components: {
    EditLibraryFileDialog,
    EditFilesAttributesDialog,
    ConfirmDeleteDialog,
    AddToPlaylistDialog,
    EditPlaylistDialog,
    FileUploader,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin, deleteConfirmationMixin],
  data() {
    return {
      items: {
        pagination: {
          perPage: 50
        }
      },
      selected: [],
      uploadActive: false,
      dropFilesOverlay: false,
      filesDirty: [],
      totalUploadProgress: 0,
      uploading: false,
      editPlaylistDialog: false,
      addToPlaylistDialog: {
        visible: false,
        playlistId: undefined
      },
      editFilesAttributesDialog: false,
      editLibraryFileDialog: {
        visible: false,
        file: undefined
      }
    }
  },
  computed: {
    attributes() {
      return this.$store.state.attributes.data
    },
    uploadAttributeValues() {
      if (!this.filters.attributes) return []

      return Object.values(this.filters.attributes).flat()
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(LibraryFileModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Artist', value: 'artist' },
      { text: 'Title', value: 'title' },
      { text: 'Attributes', sortable: false },
      { text: 'Player', sortable: false },
      { sortable: false }
    ]

    this.TYPE_MUSIC = LibraryFileModel.TYPE_MUSIC
    this.uploadUrl = process.env.VUE_APP_API_URL + '/library-files/mass-upload'

    if (this.$route.query.add_playlist_id) {
      this.addToPlaylistDialog.playlistId = Number(this.$route.query.add_playlist_id)

      this.$store.dispatch('alert', {
        text: 'To add songs to playlist, select library items and click "Add To Playlist"',
        color: 'info'
      })
    }
  },
  async mounted() {
    this.filters.attributes = Object.fromEntries(this.attributes.map(a => ([a.id, []])))

    await this.$nextTick()

    const newAttributes = this.$route.query.attributes
    const upload = this.$route.query.upload
    if (newAttributes) {
      for (const attributeId of Object.keys(newAttributes)) {
        this.filters.attributes[attributeId] = this.filters.attributes[attributeId].concat(this.$route.query.attributes[attributeId])
      }
    }

    if (upload) {
      this.uploadActive = true

      this.$store.dispatch('alert', {
        text: 'Attributes for music upload preselected',
        color: 'info'
      })
    }
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await LibraryFiles.fetch({
        type: LibraryFileModel.TYPE_MUSIC,
        ...queryParams
      })
    },
    async startUpload() {
      // TODO add attribute values

      this.uploading = true

      try {
        await this.$refs.fileUploader.performUpload()

        this.$store.dispatch('alert', {
          text: 'All songs successfully uploaded',
          color: 'success'
        })

        this.uploadActive = false
        this.filesDirty = []

        this.fetchItems()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There was a problem uploading files',
          color: 'error'
        })

        throw e
      } finally {
        this.uploading = false
      }
    },
    async deleteFiles(fileIds) {
      this.deleting = true

      try {
        await LibraryFiles.delete(fileIds)

        this.$store.dispatch('alert', {
          text: 'Songs successfully deleted',
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false
        this.selected = []

        this.fetchItems()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There was an error deleting files',
          color: 'error'
        })
      } finally {
        this.deleting = false
      }
    }
  }

}
</script>

<style scoped>
.file-input {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: 10;
  cursor: pointer;
}
</style>
